/**
 * UnifiedIndicatorBar - Toast-style notification system for ChatWindow
 * 
 * Features:
 * - Toast notifications on desktop (bottom-right corner)
 * - Mobile-optimized positioning
 * - CSS transitions for smooth animations
 * - Auto-dismissal with configurable timers
 * - Pro member awareness (no limit warnings for Pro)
 */
import React, { useState, useRef, useCallback, useEffect } from 'react';
import { AlertCircle, Loader2, CheckCircle, XCircle, Info, Zap } from 'lucide-react';
import { useMobileViewport } from '../../hooks/useMobileViewport';

// Indicator types configuration - Shorter durations for minimal intrusion
const INDICATOR_CONFIGS = {
  ERROR: { icon: XCircle, color: 'red', defaultDuration: 3000 },
  LOADING: { icon: Loader2, color: 'emerald', defaultDuration: null },
  LIMIT_WARNING: { icon: AlertCircle, color: 'amber', defaultDuration: 2500 },
  REMAINING_MESSAGES: { icon: Zap, color: 'emerald', defaultDuration: 2000 },
  SUCCESS: { icon: CheckCircle, color: 'emerald', defaultDuration: 2000 },
  INFO: { icon: Info, color: 'blue', defaultDuration: 2500 }
};

/**
 * Simplified hook to manage indicator state - only one at a time
 */
export const useIndicatorSystem = () => {
  const [current, setCurrent] = useState(null);
  const timerRef = useRef();
  
  // Clear timer on unmount
  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, []);
  
  const showIndicator = useCallback((type, content, options = {}) => {
    // Clear any existing timer
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    
    const config = INDICATOR_CONFIGS[type];
    if (!config) {
      console.warn(`Unknown indicator type: ${type}`);
      return;
    }
    
    // Set the new indicator
    setCurrent({
      type,
      content,
      ...config,
      ...options,
      id: `${type}-${Date.now()}`
    });
    
    // Set auto-dismiss timer if duration is specified
    const duration = options.duration !== undefined 
      ? options.duration 
      : config.defaultDuration;
      
    if (duration && options.autoDismiss !== false) {
      timerRef.current = setTimeout(() => {
        setCurrent(null);
      }, duration);
    }
  }, []);
  
  const hideIndicator = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    setCurrent(null);
  }, []);
  
  const clearAllIndicators = hideIndicator; // Alias for compatibility
  
  return {
    activeIndicator: current,
    indicators: current ? [current] : [], // For compatibility
    showIndicator,
    hideIndicator,
    clearAllIndicators
  };
};

/**
 * UnifiedIndicatorBar Component - Toast Style
 */
const UnifiedIndicatorBar = ({ 
  indicator,
  onDismiss,
  position = 'bottom-right', // Changed default to bottom-right
  className = '',
  isDesktop = false
}) => {
  const { 
    safeArea, 
    isMobile,
    isKeyboardVisible 
  } = useMobileViewport();
  
  const [isVisible, setIsVisible] = useState(false);
  
  // Handle visibility transitions
  useEffect(() => {
    if (indicator) {
      // Small delay to trigger CSS transition
      requestAnimationFrame(() => {
        setIsVisible(true);
      });
    } else {
      setIsVisible(false);
    }
  }, [indicator]);
  
  if (!indicator) return null;
  
  const Icon = indicator.icon;
  const isLoading = indicator.type === 'LOADING';
  
  // Calculate position for toast style - Minimal and non-intrusive
  const getPositionStyles = () => {
    if (isDesktop && !isMobile) {
      // Desktop: Bottom-right corner, minimal and unobtrusive
      return {
        position: 'fixed',
        bottom: 24,
        right: 24,
        maxWidth: '320px',
        zIndex: 999, // Just below modal z-index
      };
    } else {
      // Mobile: Bottom sheet style, above input
      return {
        position: 'fixed',
        bottom: safeArea.bottom + 100, // Above mobile input area
        left: 12,
        right: 12,
        maxWidth: '100%',
        zIndex: 999,
      };
    }
  };
  
  // Color schemes - Minimal terminal glassmorphism from design system
  const getColorClasses = () => {
    // Ultra-minimal glass with subtle borders
    const baseClass = 'bg-white/[0.03] backdrop-blur-sm border';

    const colorMap = {
      red: `${baseClass} border-red-500/20 text-white/70`,
      emerald: `${baseClass} border-emerald-500/20 text-white/70`,
      amber: `${baseClass} border-white/[0.08] text-white/50`, // Even more subtle for warnings
      blue: `${baseClass} border-white/[0.05] text-white/50`
    };

    return colorMap[indicator.color] || colorMap.emerald;
  };

  // Icon color classes - Subtle accents
  const getIconColorClasses = () => {
    const colorMap = {
      red: 'text-red-400/70',
      emerald: 'text-emerald-400/70',
      amber: 'text-white/30', // Very subtle for warnings
      blue: 'text-violet-400/50'
    };

    return colorMap[indicator.color] || 'text-white/30';
  };
  
  return (
    <div
      style={{
        ...getPositionStyles(),
        transform: isVisible ? 'translateY(0)' : 'translateY(10px)'
      }}
      className={`
        unified-indicator-toast
        transition-all duration-200 ease-out
        ${isVisible ? 'opacity-100' : 'opacity-0'}
        ${isKeyboardVisible && isMobile ? 'pointer-events-none' : ''}
        rounded-lg shadow-sm
        ${className}
      `}
    >
      <div className={`
        flex items-center gap-2.5 px-3 py-2
        ${getColorClasses()}
        rounded-lg
        ${isMobile ? 'text-sm' : 'text-sm'}
        font-medium
      `}>
        {/* Ultra-minimal indicator */}
        {isLoading ? (
          <div className="text-white/20 animate-spin">
            <Icon className="w-3.5 h-3.5" />
          </div>
        ) : indicator.type === 'REMAINING_MESSAGES' || indicator.type === 'LIMIT_WARNING' ? (
          // Just a subtle dot for message counts
          <div className="w-1 h-1 bg-white/20 rounded-full animate-pulse" />
        ) : (
          <Icon className={`w-3.5 h-3.5 ${getIconColorClasses()}`} />
        )}

        {/* Terminal-style monospace text - Bright violet for visibility */}
        <span className="font-mono text-[10px] tracking-wider text-violet-400/90 uppercase">
          {indicator.content}
        </span>
      </div>
    </div>
  );
};

/**
 * Helper component for remaining messages with smart formatting
 */
export const RemainingMessagesIndicator = ({ 
  remainingMessages, 
  showIndicator,
  isPro = false,  // Simple boolean - Pro members don't see limits
  isInitializing = false  // Skip during initialization to prevent race conditions
}) => {
  useEffect(() => {
    // Debug logging (uncomment to verify Pro users don't see message limits)
    // console.log('[RemainingMessagesIndicator] Props:', {
    //   remainingMessages,
    //   isPro,
    //   isInitializing,
    //   skipReasons: {
    //     isInitializing,
    //     isPro: isPro === true,
    //     remainingUndefined: remainingMessages === undefined || remainingMessages === null
    //   }
    // });
    
    // CRITICAL: Check isInitializing to prevent race condition where Pro users
    // briefly see "3 messages" toast while their tier data is still loading
    // Skip showing message limits during initialization or for Pro members
    if (isInitializing || isPro === true || remainingMessages === undefined || remainingMessages === null) {
      return;
    }

    // Only show for basic users when messages are VERY low (more subtle)
    if (!isPro && remainingMessages === 1) {
      // Final message warning - brief and minimal
      showIndicator(
        'LIMIT_WARNING',
        `Last message`,
        { duration: 2000 }
      );
    } else if (!isPro && remainingMessages === 2) {
      // Penultimate warning - very brief
      showIndicator(
        'REMAINING_MESSAGES',
        `${remainingMessages} left`,
        { duration: 1500 }
      );
    }
    // Don't show anything if > 2 messages - much less intrusive
  }, [remainingMessages, showIndicator, isPro, isInitializing]);
  
  return null;
};

/**
 * Helper component for loading states with context
 */
export const LoadingStateIndicator = ({
  isLoading,
  loadingText = 'Processing...',
  showIndicator,
  hideIndicator
}) => {
  const loadingIdRef = useRef(null);
  const timeoutRef = useRef(null);

  useEffect(() => {
    if (isLoading) {
      // Only show loading indicator after 500ms delay to avoid flashing
      timeoutRef.current = setTimeout(() => {
        showIndicator('LOADING', loadingText, { autoDismiss: false });
        loadingIdRef.current = true;
      }, 500);
    } else {
      // Clear timeout if loading finished quickly
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      // Hide indicator if it was shown
      if (loadingIdRef.current) {
        hideIndicator();
        loadingIdRef.current = false;
      }
    }

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      if (loadingIdRef.current) {
        hideIndicator();
      }
    };
  }, [isLoading, loadingText, showIndicator, hideIndicator]);
  
  return null;
};

export default UnifiedIndicatorBar;